import React, { useState, useEffect, useRef } from 'react';
import { Filter, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * FilterPanel — collapsible filter sidebar for any report.
 *
 * Props:
 *   filterConfig  {Array}    — [{ key, label, type, options }]
 *                              type: 'multiselect' | 'toggle' | 'text-search' | 'daterange'
 *   activeFilters {Object}   — current filter state keyed by filter.key
 *   onChange      {Function} — called with the full updated activeFilters object
 */
export function FilterPanel({ filterConfig = [], activeFilters = {}, onChange }) {
  // Expanded by default on desktop, collapsed on mobile
  const [expanded, setExpanded] = useState(() => window.innerWidth >= 1024);

  // Count active filters
  const activeCount = filterConfig.filter(f => {
    const v = activeFilters[f.key];
    if (v === undefined || v === null) return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'string') return v.trim() !== '';
    if (typeof v === 'boolean') return v !== false;
    return false;
  }).length;

  // Sync filter state to URL query params for shareability
  function syncToURL(key, value) {
    try {
      const params = new URLSearchParams(window.location.search);
      if (Array.isArray(value) && value.length) {
        params.set(key, value.join(','));
      } else if (value && value !== '' && value !== false) {
        params.set(key, String(value));
      } else {
        params.delete(key);
      }
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState(null, '', newUrl);
    } catch {
      // Non-critical — URL sync best-effort only
    }
  }

  function handleChange(key, value) {
    const next = { ...activeFilters, [key]: value };
    onChange?.(next);
    syncToURL(key, value);
  }

  function clearAll() {
    const cleared = {};
    filterConfig.forEach(f => {
      cleared[f.key] = f.type === 'multiselect' ? [] : '';
    });
    onChange?.(cleared);
    // Clear URL params
    try {
      const params = new URLSearchParams(window.location.search);
      filterConfig.forEach(f => params.delete(f.key));
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState(null, '', newUrl);
    } catch {}
  }

  return (
    <div
      data-testid="filter-panel"
      style={{
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        background: '#fff',
        overflow: 'visible',
      }}
    >
      {/* Toggle header */}
      <div
        data-testid="filter-toggle"
        role="button"
        tabIndex={0}
        onClick={() => setExpanded(e => !e)}
        onKeyDown={e => e.key === 'Enter' && setExpanded(v => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '9px 14px',
          cursor: 'pointer',
          borderBottom: expanded ? '1px solid #e2e8f0' : 'none',
          userSelect: 'none',
        }}
      >
        <Filter size={13} style={{ color: '#64748b', flexShrink: 0 }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
          Filters
          {activeCount > 0 && (
            <span
              data-testid="filter-count-badge"
              style={{
                marginLeft: 6,
                background: '#6366f1',
                color: '#fff',
                borderRadius: 10,
                padding: '1px 7px',
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              {activeCount}
            </span>
          )}
        </span>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          {activeCount > 0 && (
            <button
              data-testid="clear-filters-btn"
              onClick={e => { e.stopPropagation(); clearAll(); }}
              style={{
                fontSize: 11,
                color: '#ef4444',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '2px 6px',
                borderRadius: 4,
                fontWeight: 600,
              }}
            >
              Clear all
            </button>
          )}
          {expanded
            ? <ChevronUp size={14} style={{ color: '#64748b' }} />
            : <ChevronDown size={14} style={{ color: '#64748b' }} />
          }
        </div>
      </div>

      {/* Filter body */}
      {expanded && (
        <div
          style={{
            padding: '12px 14px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 16,
            alignItems: 'flex-start',
          }}
        >
          {filterConfig.map(filter => (
            <FilterControl
              key={filter.key}
              config={filter}
              value={activeFilters[filter.key]}
              onChange={v => handleChange(filter.key, v)}
            />
          ))}
        </div>
      )}
    </div>
  );
}


// ── Individual filter type renderers ─────────────────────────────────────────

function FilterLabel({ label }) {
  return (
    <span style={{
      fontSize: 10,
      fontWeight: 700,
      color: '#64748b',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      display: 'block',
      marginBottom: 4,
    }}>
      {label}
    </span>
  );
}

function FilterControl({ config, value, onChange }) {
  const { key, label, type, options = [] } = config;

  if (type === 'text-search') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 180 }}>
        <FilterLabel label={label} />
        <input
          data-testid={`filter-${key}`}
          type="text"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder={`Search ${label.toLowerCase()}…`}
          style={{
            height: 32, fontSize: 13, padding: '0 10px',
            border: '1px solid #e2e8f0', borderRadius: 6,
            outline: 'none', color: '#1e293b',
          }}
        />
      </div>
    );
  }

  if (type === 'toggle') {
    const opts = options.length ? options : ['All', 'Yes', 'No'];
    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <FilterLabel label={label} />
        <div style={{ display: 'flex', gap: 5 }}>
          {opts.map(opt => (
            <button
              key={opt}
              data-testid={`filter-${key}-${String(opt).toLowerCase()}`}
              onClick={() => onChange(value === opt ? '' : opt)}
              style={{
                height: 30, padding: '0 11px', fontSize: 12, borderRadius: 6,
                border: `1px solid ${value === opt ? '#6366f1' : '#e2e8f0'}`,
                background: value === opt ? '#6366f1' : '#fff',
                color: value === opt ? '#fff' : '#475569',
                cursor: 'pointer', fontWeight: 500,
                transition: 'all 0.12s',
              }}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (type === 'multiselect') {
    const selected = Array.isArray(value) ? value : [];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 160 }}>
        <FilterLabel label={label} />
        <MultiSelectDropdown
          testId={`filter-${key}`}
          options={options}
          selected={selected}
          onChange={onChange}
          placeholder={label}
        />
      </div>
    );
  }

  if (type === 'daterange') {
    const [from, to] = Array.isArray(value) ? value : ['', ''];
    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <FilterLabel label={label} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            data-testid={`filter-${key}-from`}
            type="date"
            value={from || ''}
            onChange={e => onChange([e.target.value, to])}
            style={{
              height: 30, fontSize: 12, padding: '0 7px',
              border: '1px solid #e2e8f0', borderRadius: 6, outline: 'none',
            }}
          />
          <span style={{ color: '#94a3b8', fontSize: 11 }}>→</span>
          <input
            data-testid={`filter-${key}-to`}
            type="date"
            value={to || ''}
            onChange={e => onChange([from, e.target.value])}
            style={{
              height: 30, fontSize: 12, padding: '0 7px',
              border: '1px solid #e2e8f0', borderRadius: 6, outline: 'none',
            }}
          />
        </div>
      </div>
    );
  }

  return null;
}


// ── Multi-select dropdown ─────────────────────────────────────────────────────

function MultiSelectDropdown({ testId, options, selected, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  function toggle(opt) {
    const next = selected.includes(opt)
      ? selected.filter(s => s !== opt)
      : [...selected, opt];
    onChange(next);
  }

  const label = selected.length
    ? `${selected.length} selected`
    : placeholder;

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        data-testid={testId}
        onClick={() => setOpen(o => !o)}
        style={{
          height: 32, minWidth: 150, maxWidth: 220, padding: '0 10px',
          border: '1px solid #e2e8f0', borderRadius: 6,
          background: '#fff', cursor: 'pointer', fontSize: 13,
          color: selected.length ? '#1e293b' : '#94a3b8',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
          overflow: 'hidden',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </span>
        <ChevronDown size={12} style={{ flexShrink: 0 }} />
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 36, left: 0, zIndex: 100,
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          minWidth: 200, maxHeight: 280, overflowY: 'auto',
          padding: '4px 0',
        }}>
          {options.length === 0 && (
            <div style={{ padding: '10px 12px', fontSize: 12, color: '#94a3b8' }}>
              No options available
            </div>
          )}
          {options.map(opt => {
            const isSelected = selected.includes(opt);
            return (
              <div
                key={opt}
                data-testid={`${testId}-opt-${opt}`}
                onClick={() => toggle(opt)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 12px', cursor: 'pointer', fontSize: 13,
                  background: isSelected ? '#f1f5f9' : '#fff',
                  color: '#1e293b',
                  transition: 'background 0.1s',
                }}
                onMouseEnter={e => !isSelected && (e.currentTarget.style.background = '#f8fafc')}
                onMouseLeave={e => e.currentTarget.style.background = isSelected ? '#f1f5f9' : '#fff'}
              >
                {/* Custom checkbox */}
                <div style={{
                  width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                  border: `2px solid ${isSelected ? '#6366f1' : '#cbd5e1'}`,
                  background: isSelected ? '#6366f1' : '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.1s',
                }}>
                  {isSelected && (
                    <svg viewBox="0 0 10 8" width="8" height="8" fill="none">
                      <path d="M1 4l2.5 2.5L9 1" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
                {opt}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
